import type { IControlSettingData, PayloadControlChange } from '~/presentation/reports/common/type';
import type { OrderByFields } from '~/presentation/reports/ui/ReportTable/types';
import type { SelectValueType } from '~/presentation/reports/ui/type';

import { deepCopy } from '~/utils/deepCopy';
import { create } from 'zustand';

export interface IReportControl {
  reportControlData?: IControlSettingData;
  openReportControl: boolean;
  orderBy?: OrderByFields;
  changeOrderBy: (orderBy: OrderByFields, metrics: string[]) => void;
  addOrderBy: (orderBy: OrderByFields) => void;
  toggleReportControl: () => void;
  setReportControlData: (reportControlData: IControlSettingData) => void;
  addReportControlData: (data: { controlID: string; value: SelectValueType }[]) => void;
  onControlChange: (data: PayloadControlChange) => void;
  resetData: () => void;
}

const INIT_DATA = {
  reportControlData: undefined,
  openReportControl: true,
  orderBy: undefined,
};

export const useReportControlStore = create<IReportControl>()((set) => ({
  ...INIT_DATA,
  changeOrderBy: (newOrderBy, metrics) =>
    set((prev) => {
      const [[field, orderConfig]] = Object.entries(newOrderBy);

      const isFieldMetric = metrics.includes(field);

      const orderBy = Object.fromEntries(
        Object.entries(prev.orderBy || {}).filter(([existingField]) => {
          const isExistingMetric = metrics.includes(existingField);

          if (!isExistingMetric) return true;

          if (isFieldMetric && existingField === field) return true;

          return false;
        }),
      );

      return { orderBy: { ...orderBy, [field]: orderConfig } };
    }),
  addOrderBy: (newOrderBy) => set({ orderBy: newOrderBy }),
  toggleReportControl: () => set((prev) => ({ openReportControl: !prev.openReportControl })),
  setReportControlData: (reportControlData) => set({ reportControlData }),
  addReportControlData: (data) => {
    set((prev) => {
      const reportControlData = deepCopy(prev.reportControlData) || {};
      data.forEach((item) => {
        const oldValue = reportControlData[item.controlID].value;
        reportControlData[item.controlID] = {
          ...reportControlData[item.controlID],
          value: oldValue && Array.isArray(oldValue) ? [...oldValue, ...item.value] : item.value,
        };
      });
      return {
        reportControlData,
      };
    });
  },
  onControlChange: (values) => {
    set((prev) => {
      const entries = Object.entries(values);
      const reportControlData = deepCopy(prev.reportControlData) || {};

      for (const [key, value] of entries) {
        reportControlData[key] = {
          ...reportControlData[key],
          value,
        };
      }

      return {
        reportControlData,
      };
    });
  },
  resetData: () => set({ reportControlData: undefined }),
}));
